// src/autodesk_usage/hooks/useCompanyMonthlyUsage.ts
// All comments in English only
import { useQuery } from "@tanstack/react-query";
import {
  getCompanyMonthlyUsage,
  type CorporateMonthlyUsageRow,
} from "../api/corporate";

export interface UseCompanyMonthlyUsageResult {
  data?: CorporateMonthlyUsageRow[];
  isLoading: boolean;
  error: unknown;
}

function normalizeCompanyNames(value?: string | string[] | null) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return Array.from(new Set(list.map((item) => item.trim()).filter(Boolean))).sort();
}

function normalizeCompanyMonthlyUsagePayload(
  payload: unknown
): CorporateMonthlyUsageRow[] {
  if (Array.isArray(payload)) {
    return payload as CorporateMonthlyUsageRow[];
  }
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as any).items)
  ) {
    return (payload as any).items as CorporateMonthlyUsageRow[];
  }
  return [];
}

export function useCompanyMonthlyUsage(params?: {
  companyName?: string | string[];
  startMonth?: string;
  endMonth?: string;
}): UseCompanyMonthlyUsageResult {
  const companyNames = normalizeCompanyNames(params?.companyName);
  const companyKey = companyNames.join("|");
  const query = useQuery<CorporateMonthlyUsageRow[]>({
    queryKey: [
      "company-monthly-usage",
      companyKey,
      params?.startMonth,
      params?.endMonth,
    ],
    queryFn: async () => {
      if (!companyNames.length) return [];
      const res = await getCompanyMonthlyUsage(
        companyNames,
        params.startMonth,
        params.endMonth
      );
      return normalizeCompanyMonthlyUsagePayload(res.data);
    },
    enabled: companyNames.length > 0,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
